import {
  throwOnApiErrorResponse,
  throwOnErrorResponse
} from "../chunk-NVQHPHNR.js";
import "../chunk-W4TWOJ4Y.js";
import {
  ensureDeviceAuthorized,
  ensureLoggedIn
} from "../chunk-UEDPR2CR.js";
import "../chunk-KVCLZREV.js";
import {
  signedFetch
} from "../chunk-2CSC3FHL.js";

// lib/api/fetch.ts
import {
  parseJsonResponse,
  parseOptionalJsonResponse
} from "@lune/common/api";
import { logDebug, logResults } from "@lune/common/logger";
import { useCallback, useEffect, useRef, useState } from "react";
var delayLoadMs = 0;
var maxConcurrentRequests = 20;
var maxConcurrentRequestsForTesting = maxConcurrentRequests;
var activeRequestCount = 0;
var queuedRequestResolvers = [];
var acquireRequestSlot = async () => {
  if (activeRequestCount < maxConcurrentRequests) {
    activeRequestCount++;
    return;
  }
  await new Promise((resolve) => {
    queuedRequestResolvers.push(resolve);
  });
  activeRequestCount++;
};
var releaseRequestSlot = () => {
  activeRequestCount--;
  const next = queuedRequestResolvers.shift();
  if (next != null) {
    next();
  }
};
var queuedFetch = async (input, init) => {
  await acquireRequestSlot();
  try {
    return await signedFetch(input, init);
  } finally {
    releaseRequestSlot();
  }
};
var FetchSingleErrorNotFound = "not-found";
var itemMethods = (itemUrl, loginUrl) => new Proxy(
  {},
  {
    get: (_target, method) => ({
      get: async (body) => await methodRead(`${itemUrl}:${method}`, body, loginUrl),
      post: async (body) => await methodSend(
        "POST",
        `${itemUrl}:${method}`,
        body,
        loginUrl
      ),
      patch: async (body) => await methodSend(
        "PATCH",
        `${itemUrl}:${method}`,
        body,
        loginUrl
      )
    })
  }
);
var newApi = (url, loginUrl) => ({
  ...newApiReadOnly(url, loginUrl),
  create: async (data, headers) => await create(url, data, loginUrl, headers),
  remove: async (data) => {
    await remove(url, data, loginUrl);
  },
  update: async (id, data, headers) => {
    await update(url, id, data, loginUrl, headers);
  },
  item: (id) => ({
    methods: itemMethods(url.api.item(id), loginUrl)
  })
});
var newApiReadOnly = (url, loginUrl) => ({
  fetch: {
    single: async (id) => await single(url, id, loginUrl),
    useSingle: (clockTick, id) => useSingle(clockTick, url, id, loginUrl),
    all: async () => await all(url, loginUrl),
    useAll: (clockTick) => useAll(clockTick, url, loginUrl),
    list: async () => await list(url, loginUrl),
    useList: (clockTick) => useList(clockTick, url, loginUrl)
  }
});
var newContainedApi = (url, loginUrl) => ({
  all: async (container) => await allContained(url, container, loginUrl),
  create: async (container, data, headers) => await createContained(url, container, data, loginUrl, headers),
  remove: async (id) => await removeContained(url, id, loginUrl),
  update: async (id, data, headers) => {
    await updateContained(url, id, data, loginUrl, headers);
  },
  fetch: {
    single: async (id) => await singleContained(url, id, loginUrl),
    useSingle: (clockTick, id) => useSingleContained(clockTick, url, id, loginUrl),
    useAll: (clockTick, container) => useAllContained(clockTick, url, container, loginUrl)
  },
  item: (id) => ({
    methods: itemMethods(url.api.item(id), loginUrl)
  })
});
var methodRead = async (url, body, loginUrl) => {
  const finalUrl = body ? `${url}?${new URLSearchParams(body).toString()}` : url;
  return await queuedFetch(finalUrl).then(ensureLoggedIn(loginUrl)).then(ensureDeviceAuthorized).then(throwOnErrorResponse).then(async (res) => await parseJsonResponse(res)).then(throwOnApiErrorResponse).then((res) => res.item);
};
var methodSend = async (verb, url, body, loginUrl) => {
  return await queuedFetch(url, {
    method: verb,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body)
  }).then(ensureLoggedIn(loginUrl)).then(ensureDeviceAuthorized).then(throwOnErrorResponse).then((res) => parseOptionalJsonResponse(res)).then(
    (res) => res !== null ? throwOnApiErrorResponse(res).item : void 0
  );
};
var single = async (url, id, loginUrl) => {
  logDebug("[fetch] Fetching (single)", url.kind, id, url.api.item(id));
  return await queuedFetch(url.api.item(id)).then(logResults(`[fetch] api/${url.kind}/get`)).then(ensureLoggedIn(loginUrl)).then(ensureDeviceAuthorized).then(throwOnErrorResponse).then(async (result) => await parseJsonResponse(result)).then(throwOnApiErrorResponse).then(logResults(`[fetch] api/${url.kind}/get:json`)).then((result) => result.item).then(logResults(`[fetch] api/${url.kind}/get:json:item`));
};
var useSingle = (clockTick, url, id, loginUrl) => {
  const [item, setItem] = useState();
  const [ready, setReady] = useState(false);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [error, setError] = useState();
  const [errorMessage, setErrorMessage] = useState();
  const [timestamp, setTimestamp] = useState();
  const [firstLoad, setFirstLoad] = useState(true);
  const lastLoad = useRef({
    clockTick: -1,
    id: null,
    loginUrl: null,
    url: null
  });
  const log = useCallback(
    (...args) => logDebug(`[useSingle: ${url.kind}]`, ...args, clockTick, id, url),
    [clockTick, id, url]
  );
  const load = useCallback(() => {
    log("Loading single item");
    if (JSON.stringify({ clockTick, id, loginUrl, url }) === JSON.stringify(lastLoad.current)) {
      log("Skipping load [data] === [lastLoad]");
      return;
    }
    lastLoad.current = { clockTick, id, loginUrl, url };
    single(url, id, loginUrl).then((result2) => {
      setTimeout(() => {
        setItem(result2);
        setReady(true);
        setError(void 0);
        setErrorMessage(void 0);
        setTimestamp(Date.now());
      }, delayLoadMs);
    }).catch((err) => {
      switch (err.code) {
        case 401:
          log("Error 401");
          setReady(false);
          setNeedsLogin(true);
          setTimestamp(void 0);
          setError("unauthorized");
          break;
        case 404:
          log("Error 404");
          setReady(true);
          setNeedsLogin(false);
          setTimestamp(void 0);
          setError("not-found");
          setErrorMessage(`Item not found [${id}]`);
          break;
        default:
          setError("other");
          setErrorMessage(`Error reading data :-( [${err.message}]`);
          break;
      }
    });
  }, [log, clockTick, id, loginUrl, url]);
  useEffect(() => {
    if (firstLoad) {
      log("Running first load");
      setFirstLoad(false);
      load();
    }
  }, [firstLoad, load, log]);
  useEffect(() => {
    const idChanged = JSON.stringify(id) !== JSON.stringify(lastLoad.current.id);
    if (clockTick > lastLoad.current.clockTick || idChanged) {
      if (idChanged) {
        setReady(false);
        setItem(void 0);
        setError(void 0);
        setErrorMessage(void 0);
      }
      load();
    }
  }, [clockTick, id, load]);
  const reload = useCallback(() => {
    log("Reloading");
    setReady(false);
    setItem(void 0);
    setError(void 0);
    setErrorMessage(void 0);
    lastLoad.current.clockTick = -1;
    load();
  }, [load, log]);
  const result = {
    ready,
    needsLogin,
    error,
    errorMessage,
    reload,
    item,
    timestamp
  };
  return result;
};
var all = async (url, loginUrl) => {
  logDebug("[fetch] Fetching (multiple)", url.kind, url.api.collection);
  return await queuedFetch(url.api.collection).then(logResults(`[fetch] api/${url.kind}/get:all`)).then(ensureLoggedIn(loginUrl)).then(ensureDeviceAuthorized).then(throwOnErrorResponse).then(async (result) => await parseJsonResponse(result)).then(throwOnApiErrorResponse).then(logResults(`[fetch] api/${url.kind}/list:json`)).then((result) => result.items ?? []).then(logResults(`[fetch] api/${url.kind}/get:json:items`));
};
var useAll = (clockTick, url, loginUrl) => {
  const [allItems, setAllItems] = useState();
  const [ready, setReady] = useState(false);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [timestamp, setTimestamp] = useState();
  const lastLoad = useRef({
    clockTick: -1,
    loginUrl: null,
    url: null
  });
  const log = useCallback(
    (...args) => logDebug(`[useAll: ${url.kind}]`, ...args, clockTick, url),
    [clockTick, url]
  );
  const load = useCallback(() => {
    log("Loading all items");
    if (JSON.stringify({ clockTick, loginUrl, url }) === JSON.stringify(lastLoad.current)) {
      log("Skipping load [data] === [lastLoad]");
      return;
    }
    lastLoad.current = { clockTick, loginUrl, url };
    all(url, loginUrl).then((result2) => {
      setTimeout(() => {
        setAllItems([...result2 ?? []]);
        setReady(true);
        setTimestamp(Date.now());
      }, delayLoadMs);
    }).catch((err) => {
      switch (err.code) {
        case 401:
          log("Error 401");
          setReady(false);
          setNeedsLogin(true);
          setTimestamp(void 0);
          break;
        default:
          log("Fetch error");
          setReady(false);
          setNeedsLogin(true);
          setTimestamp(void 0);
          setErrorMessage(`Error reading data :-( [${err.message}]`);
          break;
      }
    });
  }, [log, loginUrl, url, clockTick]);
  useEffect(() => {
    load();
  }, [load]);
  const inlineUpdateItem = ({
    id,
    data,
    comparer
  }) => {
    log("Inline update item", id, data);
    setAllItems((prevAllItems) => [
      ...(prevAllItems ?? []).filter((item) => !comparer(id, item)),
      data
    ]);
  };
  const reload = () => {
    log("Reloading");
    setReady(false);
    setAllItems(void 0);
    setErrorMessage(void 0);
    lastLoad.current.clockTick = -1;
    load();
  };
  const result = {
    ready,
    needsLogin,
    errorMessage,
    inlineUpdateItem,
    reload,
    items: allItems ?? [],
    timestamp
  };
  return result;
};
var list = async (url, loginUrl) => {
  logDebug("[fetch] Fetching (multiple)", url.kind, url.api.collection);
  return await queuedFetch(`${url.api.collection}!INDEX`).then(logResults(`[fetch] api/${url.kind}!INDEX`)).then(ensureLoggedIn(loginUrl)).then(ensureDeviceAuthorized).then(throwOnErrorResponse).then(async (result) => await parseJsonResponse(result)).then(throwOnApiErrorResponse).then(logResults(`[fetch] api/${url.kind}/list:json`)).then((result) => result.items ?? []).then(logResults(`[fetch] api/${url.kind}/get:json:items`));
};
var useList = (clockTick, url, loginUrl) => {
  const [allItems, setAllItems] = useState();
  const [ready, setReady] = useState(false);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [timestamp, setTimestamp] = useState();
  const lastLoad = useRef({
    clockTick: -1,
    loginUrl: null,
    url: null
  });
  const load = useCallback(() => {
    if (JSON.stringify({ clockTick, loginUrl, url }) === JSON.stringify(lastLoad.current)) {
      return;
    }
    lastLoad.current = { clockTick, loginUrl, url };
    list(url, loginUrl).then((result) => {
      setTimeout(() => {
        setReady(true);
        setAllItems(result);
      }, delayLoadMs);
    }).catch((err) => {
      switch (err.code) {
        case 401:
          setReady(false);
          setNeedsLogin(true);
          setTimestamp(void 0);
          break;
        default:
          setReady(false);
          setNeedsLogin(true);
          setTimestamp(void 0);
          setErrorMessage(`Error reading data :-( [${err.message}]`);
          break;
      }
    });
  }, [loginUrl, url, clockTick]);
  useEffect(() => {
    load();
  }, [load]);
  return {
    ready,
    needsLogin,
    errorMessage,
    inlineUpdateItem: () => {
      throw new Error("useList does not support inline updates");
    },
    reload: () => {
      setReady(false);
      setAllItems(void 0);
      setErrorMessage(void 0);
      lastLoad.current.clockTick = -1;
      load();
    },
    items: allItems ?? [],
    timestamp
  };
};
var create = async (url, data, loginUrl, headers) => {
  logDebug("[fetch] Creating", url.kind, data);
  return await queuedFetch(url.api.collection, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...headers
    },
    body: JSON.stringify(data)
  }).then(logResults(`[fetch] api/${url.kind}/create`)).then(ensureLoggedIn(loginUrl)).then(ensureDeviceAuthorized).then(throwOnErrorResponse).then(logResults(`[fetch] api/${url.kind}/create = ok`)).then((result) => parseOptionalJsonResponse(result)).then(
    (result) => result !== null ? throwOnApiErrorResponse(result).item : data
  ).then(logResults(`[fetch] api/${url.kind}/create:json:items`));
};
var update = async (url, id, data, loginUrl, headers) => {
  logDebug("[fetch] Creating", url.kind, data);
  return await queuedFetch(url.api.item(id), {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...headers
    },
    body: JSON.stringify(data)
  }).then(logResults(`[fetch] api/${url.kind}/update`)).then(ensureLoggedIn(loginUrl)).then(ensureDeviceAuthorized).then(throwOnErrorResponse).then(logResults(`[fetch] api/${url.kind}/update = ok`)).then((result) => parseOptionalJsonResponse(result)).then(
    (result) => result !== null ? throwOnApiErrorResponse(result).item : data
  ).then(logResults(`[fetch] api/${url.kind}/update:json:items`));
};
var remove = async (url, id, loginUrl) => {
  logDebug("[fetch] Removing", url.kind, id, url.api.item(id));
  await queuedFetch(url.api.item(id), {
    method: "DELETE"
  }).then(logResults(`[fetch] api/${url.kind}/remove`)).then(ensureLoggedIn(loginUrl)).then(ensureDeviceAuthorized).then(throwOnErrorResponse).then(logResults(`[fetch] api/${url.kind}/remove = ok`)).then(() => {
  });
};
var singleContained = async (url, id, loginUrl) => {
  logDebug(
    "[fetch] Fetching (single:contained)",
    url.kind,
    id,
    url.api.item(id)
  );
  return await queuedFetch(url.api.item(id)).then(logResults(`[fetch] api/${url.kind}/get`)).then(ensureLoggedIn(loginUrl)).then(ensureDeviceAuthorized).then(throwOnErrorResponse).then(async (result) => await parseJsonResponse(result)).then(throwOnApiErrorResponse).then(logResults(`[fetch] api/${url.kind}/get:json`)).then((result) => result.item).then(logResults(`[fetch] api/${url.kind}/get:json:item`));
};
var useSingleContained = (clockTick, url, id, loginUrl) => {
  const [item, setItem] = useState();
  const [ready, setReady] = useState(false);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [error, setError] = useState();
  const [errorMessage, setErrorMessage] = useState();
  const [timestamp, setTimestamp] = useState();
  const [firstLoad, setFirstLoad] = useState(true);
  const lastLoad = useRef({
    clockTick: -1,
    id: null,
    loginUrl: null,
    url: null
  });
  const log = useCallback(
    (...args) => logDebug(
      `[useSingleContained: ${url.kind}]`,
      ...args,
      clockTick,
      id,
      url
    ),
    [clockTick, id, url]
  );
  const load = useCallback(() => {
    log("Loading single item (contained)");
    if (JSON.stringify({ clockTick, id, loginUrl, url }) === JSON.stringify(lastLoad.current)) {
      log("Skipping load [data] === [lastLoad]");
      return;
    }
    lastLoad.current = { clockTick, id, loginUrl, url };
    singleContained(url, id, loginUrl).then((result2) => {
      setTimeout(() => {
        setItem(result2);
        setReady(true);
        setError(void 0);
        setErrorMessage(void 0);
        setTimestamp(Date.now());
      }, delayLoadMs);
    }).catch((err) => {
      switch (err.code) {
        case 401:
          log("Error 401");
          setReady(false);
          setNeedsLogin(true);
          setTimestamp(void 0);
          break;
        case 404:
          log("Error 404");
          setReady(true);
          setNeedsLogin(false);
          setTimestamp(void 0);
          setError("not-found");
          setErrorMessage(`Item not found [${id}]`);
          break;
        default:
          setError("other");
          setErrorMessage(`Error reading data :-( [${err.message}]`);
          break;
      }
    });
  }, [log, clockTick, id, loginUrl, url]);
  useEffect(() => {
    if (firstLoad) {
      log("Running first load");
      setFirstLoad(false);
      load();
    }
  }, [firstLoad, load, log]);
  useEffect(() => {
    const idChanged = JSON.stringify(id) !== JSON.stringify(lastLoad.current.id);
    if (clockTick > lastLoad.current.clockTick || idChanged) {
      if (idChanged) {
        setReady(false);
        setItem(void 0);
        setError(void 0);
        setErrorMessage(void 0);
      }
      load();
    }
  }, [clockTick, id, load]);
  const reload = useCallback(() => {
    log("Reloading");
    setReady(false);
    setItem(void 0);
    setError(void 0);
    setErrorMessage(void 0);
    lastLoad.current.clockTick = -1;
    load();
  }, [load]);
  const result = {
    ready,
    needsLogin,
    error,
    errorMessage,
    reload,
    item,
    timestamp
  };
  return result;
};
var allContained = async (url, container, loginUrl) => {
  logDebug(
    "[fetch] Fetching (multiple:contained)",
    url.kind,
    url.api.collection(container)
  );
  return await queuedFetch(url.api.collection(container)).then(logResults(`[fetch] api/${url.kind}/get:all`)).then(ensureLoggedIn(loginUrl)).then(ensureDeviceAuthorized).then(throwOnErrorResponse).then(async (result) => await parseJsonResponse(result)).then(throwOnApiErrorResponse).then(logResults(`[fetch] api/${url.kind}/list:json`)).then((result) => result.items ?? []).then(logResults(`[fetch] api/${url.kind}/get:json:items`));
};
var useAllContained = (clockTick, url, container, loginUrl) => {
  const [allItems, setAllItems] = useState();
  const [ready, setReady] = useState(false);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [timestamp, setTimestamp] = useState();
  const lastLoad = useRef({
    clockTick: -1,
    container: null,
    loginUrl: null,
    url: null
  });
  const log = useCallback(
    (...args) => logDebug(`[useAllContained: ${url.kind}]`, ...args, clockTick, url),
    [clockTick, url]
  );
  const load = useCallback(() => {
    log("Loading all items (contained)", container);
    if (JSON.stringify({ clockTick, container, loginUrl, url }) === JSON.stringify(lastLoad.current)) {
      log("Skipping load [data] === [lastLoad]");
      return;
    }
    lastLoad.current = { clockTick, container, loginUrl, url };
    allContained(url, container, loginUrl).then((result2) => {
      setTimeout(() => {
        setAllItems([...result2 ?? []]);
        setReady(true);
        setTimestamp(Date.now());
      }, delayLoadMs);
    }).catch((err) => {
      switch (err.code) {
        case 401:
          log("Error 401");
          setReady(false);
          setNeedsLogin(true);
          setTimestamp(void 0);
          break;
        default:
          log("Fetch error");
          setReady(false);
          setNeedsLogin(true);
          setTimestamp(void 0);
          setErrorMessage(`Error reading data :-( [${err.message}]`);
          break;
      }
    });
  }, [log, loginUrl, url, container, clockTick]);
  useEffect(() => {
    load();
  }, [load]);
  const inlineUpdateItem = ({
    id,
    data,
    comparer
  }) => {
    log("Inline update item", id, data);
    setAllItems((prevAllItems) => [
      ...(prevAllItems ?? []).filter((item) => !comparer(id, item)),
      data
    ]);
  };
  const reload = () => {
    log("Reloading");
    setReady(false);
    setAllItems(void 0);
    setErrorMessage(void 0);
    lastLoad.current.clockTick = -1;
    load();
  };
  const result = {
    ready,
    needsLogin,
    errorMessage,
    inlineUpdateItem,
    reload,
    items: allItems ?? [],
    timestamp
  };
  return result;
};
var createContained = async (url, container, data, loginUrl, headers) => {
  logDebug("[fetch] Creating (contained)", url.kind, data);
  return await queuedFetch(url.api.collection(container), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...headers
    },
    body: JSON.stringify(data)
  }).then(logResults(`[fetch] api/${url.kind}/create`)).then(ensureLoggedIn(loginUrl)).then(ensureDeviceAuthorized).then(throwOnErrorResponse).then(logResults(`[fetch] api/${url.kind}/create = ok`)).then((result) => parseOptionalJsonResponse(result)).then(
    (result) => result !== null ? throwOnApiErrorResponse(result).item : data
  ).then(logResults(`[fetch] api/${url.kind}/create:json:items`));
};
var updateContained = async (url, id, data, loginUrl, headers) => {
  logDebug("[fetch] Updating (contained)", url.kind, data);
  return await queuedFetch(url.api.item(id), {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...headers
    },
    body: JSON.stringify(data)
  }).then(logResults(`[fetch] api/${url.kind}/update`)).then(ensureLoggedIn(loginUrl)).then(ensureDeviceAuthorized).then(throwOnErrorResponse).then(logResults(`[fetch] api/${url.kind}/update = ok`)).then((result) => parseOptionalJsonResponse(result)).then(
    (result) => result !== null ? throwOnApiErrorResponse(result).item : data
  ).then(logResults(`[fetch] api/${url.kind}/update:json:items`));
};
var removeContained = async (url, id, loginUrl) => {
  logDebug("[fetch] Removing (contained)", url.kind, id, url.api.item(id));
  await queuedFetch(url.api.item(id), {
    method: "DELETE"
  }).then(logResults(`[fetch] api/${url.kind}/remove`)).then(ensureLoggedIn(loginUrl)).then(ensureDeviceAuthorized).then(throwOnErrorResponse).then(logResults(`[fetch] api/${url.kind}/remove = ok`)).then(() => {
  });
};
var singleBinary = async (url, loginUrl) => await queuedFetch(url).then(ensureLoggedIn(loginUrl)).then(ensureDeviceAuthorized).then(throwOnErrorResponse).then(async (res) => await res.blob());
var newBinaryApi = (loginUrl) => ({
  fetch: {
    single: async (url) => await singleBinary(url, loginUrl)
  }
});
var denyApi = () => ({
  create: async () => await Promise.reject(new Error("NOT ALLOWED")),
  fetch: {
    all: async () => await Promise.reject(new Error("NOT ALLOWED")),
    useAll: () => {
      throw new Error("NOT ALLOWED");
    },
    list: async () => await Promise.reject(new Error("NOT ALLOWED")),
    useList: () => {
      throw new Error("NOT ALLOWED");
    },
    single: async () => await Promise.reject(new Error("NOT ALLOWED")),
    useSingle: () => {
      throw new Error("NOT ALLOWED");
    }
  },
  remove: async () => await Promise.reject(new Error("NOT ALLOWED")),
  update: async () => await Promise.reject(new Error("NOT ALLOWED")),
  item: () => {
    throw new Error("NOT ALLOWED");
  }
});
export {
  FetchSingleErrorNotFound,
  denyApi,
  maxConcurrentRequestsForTesting,
  newApi,
  newApiReadOnly,
  newBinaryApi,
  newContainedApi
};
